import { ChangeEvent, FocusEvent, useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Criterion, Field, FieldType, Quota } from 'queries';

import { Checkbox, Input } from '@app/form';
import {
  FieldConfig,
  useFieldConfigurations,
  validationFieldTypes,
} from '../fieldConfigurations';

import { FieldTypeDropdown } from './elements/FieldTypeDropdown';
import { LogicJumpOption } from './elements/interface';
import { createCriterionForField } from 'lib/criteria';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    boxShadow: theme.boxShadow.base,
    borderRadius: theme.borderRadius.std,
    marginTop: theme.marginBase * 3,
    [theme.media.tablet]: {
      marginTop: 0,
    },
  },
  questions: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    paddingBottom: 0,
  },
  questionType: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.marginBase * 2,
  },
  labelInput: {
    paddingBottom: theme.marginBase * 2,
  },
  questionNumberContainer: {
    backgroundColor: theme.colors.blueMuted,
    borderRadius: theme.borderRadius.std,
    minHeight: theme.marginBase * 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.marginBase,
  },
  questionNumber: {
    textAlign: 'center',
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
}));

interface Props {
  field: Field;
  onChange: (field: Field) => any;
  criterion?: Criterion;
  onCriterionChange?: (criterion: Criterion) => any;
  onRemoveCriterion?: (criterion: Criterion) => any;
  fieldNumber?: number;
  logicJumpOptions?: LogicJumpOption[];
  shouldAlwaysValidate?: boolean;
  numberOfParticipantRequired?: number;
  sessionId?: string;
  quotas?: Quota[];
  onQuotaChange?: (quotas: Quota[]) => any;
}

export const FieldBuilder = ({
  field,
  onChange,
  onCriterionChange,
  criterion,
  onRemoveCriterion,
  fieldNumber,
  logicJumpOptions,
  shouldAlwaysValidate,
  numberOfParticipantRequired,
  sessionId,
  quotas,
  onQuotaChange,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const fieldConfigurations = useFieldConfigurations();

  const questionsType = useMemo(() => {
    const allQuestionsType = Object.values(fieldConfigurations).filter(
      Boolean,
    ) as FieldConfig[];

    if (shouldAlwaysValidate) {
      return allQuestionsType.filter((f) =>
        validationFieldTypes.includes(f.value),
      );
    }
    return allQuestionsType;
  }, [shouldAlwaysValidate, fieldConfigurations]);

  const _onChange = useCallback(
    (field: Field) => {
      if (onCriterionChange && field.type === FieldType.SINGLE_CHOICE) {
        onCriterionChange(createCriterionForField(field));
      }

      onChange(field);
    },
    [onChange, onCriterionChange],
  );

  const onFieldOptionsChange = (_: string, option: any) => {
    if (option.value === field.type) {
      return;
    }
    if (
      (option.value === FieldType.MULTIPLE_CHOICE &&
        field.type === FieldType.SINGLE_CHOICE) ||
      (option.value === FieldType.SINGLE_CHOICE &&
        field.type === FieldType.MULTIPLE_CHOICE)
    ) {
      _onChange({
        ...field,
        type: option.value,
      });
      return;
    }
    _onChange({
      ...field,
      type: option.value,
      properties: option.createDefaultProperties
        ? option.createDefaultProperties()
        : undefined,
    });
    if (onRemoveCriterion && criterion) {
      onRemoveCriterion(criterion);
    }
  };

  const config = fieldConfigurations[field.type] as FieldConfig<typeof field>;

  const onRequiredChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...field,
      validation: {
        ...field.validation,
        required: e.currentTarget.checked,
      },
    });
  };
  const onTitleChange = (e: FocusEvent<HTMLInputElement>) => {
    onChange({
      ...field,
      title: e.currentTarget.value,
    });
  };

  const PreFieldComponent = (
    <div className={classes.questionNumberContainer}>
      <span className={classes.questionNumber}>{fieldNumber}</span>
    </div>
  );

  return (
    <div className={classes.container}>
      <div className={classes.questions}>
        <div className={classes.questionType}>
          <FieldTypeDropdown
            onChange={onFieldOptionsChange}
            value={field.type}
            options={questionsType}
          />
          {!shouldAlwaysValidate && (
            <div>
              <Checkbox
                label={t('screeningForm.create.questionBuilder.required')}
                checked={field.validation && field.validation.required}
                noMargin
                onChange={onRequiredChange}
              />
            </div>
          )}
        </div>
        <Input
          defaultValue={field.title}
          onBlur={onTitleChange}
          label={t('screeningForm.create.questionBuilder.questionLabel')}
          placeholder=""
          containerClass={classes.labelInput}
          noMargin
          autoFocus
          small
          requiredStar
          preFieldComponent={fieldNumber && PreFieldComponent}
        />
      </div>
      {config.renderBuilder &&
        config.renderBuilder({
          field,
          onChange,
          criterion,
          onCriterionChange,
          logicJumpOptions,
          mustChooseValidation: shouldAlwaysValidate,
          numberOfParticipantRequired,
          sessionId: sessionId,
          quotas,
          onQuotaChange,
        })}
    </div>
  );
};
